import React from "react";
import { useParams } from "react-router-dom";

import PageContent from "ui/PageContent";
import Panel from "ui/Panel";
import PanelSection from "ui/PanelSection";
import SettingsNav from "ui/SettingsNav";
import Loading from "ui/Loading";
import UpgradeRequired from "ui/UpgradeRequired";
import { useFeature } from "providers/OrganizationFeatures";
import { useSetFlash } from "providers/WithFlashes";

import ApiKeysContent from "./ApiKeysContent";
import { OAuthApplicationsPanel, OAuthAdminPanel } from "./OAuthSettings";
import { AccessTokensPanel } from "./AccessTokens";
import {
  OAuthAdminDocument,
  useOAuthAdminQuery,
} from "./gql/AdminQuery.generated";
import { useUpdateOrganizationApiSettingsMutation } from "./gql/UpdateApiSettings.generated";

const ApiAccess = () => {
  const { slug: organizationSlug } = useParams();
  return (
    <PageContent
      title="Settings"
      pageCategory="api-keys"
      pageName="index"
      featureNav={<SettingsNav />}
    >
      <ApiKeysContent organizationSlug={organizationSlug} />
      <AccessTokensSection organizationSlug={organizationSlug} />
      <ApiSettingsPanels organizationSlug={organizationSlug} />
    </PageContent>
  );
};

const AccessTokensSection = ({
  organizationSlug,
}: {
  organizationSlug: string;
}) => {
  const hasAccessTokens = useFeature("accessTokens");
  if (!hasAccessTokens) {
    return null;
  }
  return <AccessTokensPanel organizationSlug={organizationSlug} />;
};

export const ApiSettingsPanels = ({
  organizationSlug,
}: {
  organizationSlug: string;
}) => {
  const { data, loading, error } = useOAuthAdminQuery({
    variables: { organizationSlug },
  });

  if (loading || error) {
    return <Loading error={!!error} />;
  }

  const { mcpEnabled, permittedToManageApiSettings } =
    data.getOrganizationDetails;

  return (
    <>
      <McpSettingsPanel
        organizationSlug={organizationSlug}
        mcpEnabled={mcpEnabled}
        permittedToManageApiSettings={permittedToManageApiSettings}
      />
      {permittedToManageApiSettings && (
        <OAuthAdminPanel organizationSlug={organizationSlug} />
      )}
      <OAuthApplicationsPanel organizationSlug={organizationSlug} />
    </>
  );
};

export const McpSettingsPanel = ({
  organizationSlug,
  mcpEnabled,
  permittedToManageApiSettings,
}: {
  organizationSlug: string;
  mcpEnabled: boolean;
  permittedToManageApiSettings: boolean;
}) => {
  const setFlash = useSetFlash();
  const hasMcpServer = useFeature("mcpServer");

  const [updateSettings, { loading: updateLoading }] =
    useUpdateOrganizationApiSettingsMutation();

  const handleToggleMcp = () => {
    const newEnabled = !mcpEnabled;
    const action = newEnabled ? "enable" : "disable";
    if (
      !window.confirm(
        `Are you sure you want to ${action} the MCP server for this organization?`,
      )
    ) {
      return;
    }
    updateSettings({
      variables: {
        organizationSlug,
        mcpEnabled: newEnabled,
      },
      onCompleted: () => {
        setFlash(
          "success",
          `MCP server has been ${newEnabled ? "enabled" : "disabled"}.`,
        );
      },
      onError: (error) => {
        setFlash("error", error.message);
      },
      refetchQueries: [
        { query: OAuthAdminDocument, variables: { organizationSlug } },
      ],
    });
  };

  return hasMcpServer ? (
    <Panel
      title="MCP Server Access (Preview)"
      secondaryTitle={
        <a href="https://pganalyze.com/docs/mcp" target="_blank">
          Learn more
        </a>
      }
    >
      <PanelSection>
        <p>
          The MCP server is{" "}
          <strong>{mcpEnabled ? "enabled" : "disabled"}</strong>. MCP clients{" "}
          {mcpEnabled ? "can" : "cannot"} access data of this organization.
        </p>
        {permittedToManageApiSettings && (
          <button
            onClick={handleToggleMcp}
            disabled={updateLoading}
            className="btn btn-success"
          >
            {mcpEnabled ? "Disable" : "Enable"} MCP Server
          </button>
        )}
      </PanelSection>
    </Panel>
  ) : (
    <UpgradeRequired
      feature="MCP Server"
      title="MCP Server Access (Preview)"
      panelOnly
    />
  );
};

export default ApiAccess;
